// menopauseScreeningData.js
// All screen definitions, questions, options and scoring for Menopause Risk Screening

export const MENOPAUSE_STEPS = [
  { id: 'intro',        label: 'Introduction' },
  { id: 'consent',      label: 'Consent' },
  { id: 'concerns',     label: 'Main Concerns' },
  { id: 'age',          label: 'Age & Menstrual Status' },
  { id: 'vasomotor',    label: 'Vasomotor Symptoms' },
  { id: 'sleep',        label: 'Sleep & Energy' },
  { id: 'mood',         label: 'Mood & Mental Health' },
  { id: 'physical',     label: 'Physical Symptoms' },
  { id: 'medical',      label: 'Medical History' },
  { id: 'review',       label: 'Review Summary' },
  { id: 'result',       label: 'Risk Result' },
];

export const MENOPAUSE_FLOW = [
  'intro', 'consent', 'concerns',
  'age', 'vasomotor', 'sleep', 'mood', 'physical', 'medical',
  'review', 'result',
];

// ── Intro ──────────────────────────────────────────────────────────────────────
export const MENOPAUSE_INTRO = {
  badge:        'Menopause Health',
  badgeIcon:    '🌸',
  heading:      "Let's understand your menopause health",
  desc:         'This screening helps assess your symptoms, quality of life and related health risks to provide personalised recommendations.',
  bullets: [
    { icon: '⏱️', text: 'Takes less than 8 minutes' },
    { icon: '🔒', text: 'Private & confidential' },
    { icon: '📋', text: 'Not a medical diagnosis' },
  ],
  privacy:      'Your health data is safe with us and will never be shared.',
  startLabel:   'Start screening',
  laterLabel:   "I'll do it later",
  illustrationKey: 'menopause',
};

// ── Consent ────────────────────────────────────────────────────────────────────
export const MENOPAUSE_CONSENT = {
  heading: 'Before we begin',
  paragraphs: [
    'This screening is for informational purposes only and not a medical diagnosis.',
    'Menopause symptoms vary widely — your answers help us personalise your experience.',
  ],
  checkLabel: 'I agree that this screening does not replace professional medical advice, diagnosis or treatment.',
  agreeLabel: 'I agree, continue',
};

// ── Main concern chips ─────────────────────────────────────────────────────────
export const MENOPAUSE_CONCERNS = [
  { id: 'hotFlushes',   label: 'Hot flushes',     icon: '🔥' },
  { id: 'nightSweats',  label: 'Night sweats',     icon: '💧' },
  { id: 'sleepIssues',  label: 'Sleep problems',   icon: '😴' },
  { id: 'moodChanges',  label: 'Mood changes',     icon: '😔' },
  { id: 'weightGain',   label: 'Weight gain',      icon: '⚖️' },
  { id: 'lowLibido',    label: 'Low libido',        icon: '💔' },
  { id: 'brainFog',     label: 'Brain fog',        icon: '🧠' },
  { id: 'jointPain',    label: 'Joint pain',       icon: '🦴' },
];

// ── Question screens ───────────────────────────────────────────────────────────
export const MENOPAUSE_QUESTION_SCREENS = [
  {
    id: 'age',
    stepLabel: 'Age & Menstrual Status',
    title: 'Age & Menstrual Status',
    subtitle: 'Your age and cycle status help us understand where you are on your menopause journey.',
    icon: '📅',
    questions: [
      {
        id: 'ageGroup',
        label: 'What is your age group?',
        type: 'toggle',
        options: [
          { label: 'Under 40',  score: 0 },
          { label: '40–44',     score: 1 },
          { label: '45–49',     score: 2 },
          { label: '50–54',     score: 3 },
          { label: '55–59',     score: 3 },
          { label: '60+',       score: 2 },
        ],
      },
      {
        id: 'menstrualStatus',
        label: 'What best describes your current menstrual status?',
        type: 'toggle',
        options: [
          { label: 'Regular periods',                          score: 0 },
          { label: 'Periods becoming irregular',               score: 2 },
          { label: 'No period for 3–11 months',               score: 3 },
          { label: 'No period for 12+ months (menopause)',     score: 3 },
          { label: 'Surgical/medical menopause',               score: 3 },
        ],
      },
      {
        id: 'periodChanges',
        label: 'Have you noticed changes in your period in the past 12 months?',
        type: 'toggle',
        options: [
          { label: 'No change',                score: 0 },
          { label: 'Lighter / shorter',        score: 1 },
          { label: 'Heavier / longer',         score: 1 },
          { label: 'More irregular',           score: 2 },
          { label: 'Periods have stopped',     score: 3 },
        ],
      },
    ],
  },

  {
    id: 'vasomotor',
    stepLabel: 'Vasomotor Symptoms',
    title: 'Hot Flushes & Night Sweats',
    subtitle: 'Vasomotor symptoms are among the most common signs of perimenopause and menopause.',
    icon: '🔥',
    questions: [
      {
        id: 'hotFlushFreq',
        label: 'How often do you experience hot flushes?',
        type: 'toggle',
        options: [
          { label: 'Never',             score: 0 },
          { label: 'Less than 1/week',  score: 1 },
          { label: '1–3 times/week',    score: 2 },
          { label: 'Daily',             score: 3 },
          { label: 'Multiple times/day',score: 4 },
        ],
      },
      {
        id: 'hotFlushSeverity',
        label: 'How severe are your hot flushes?',
        type: 'toggle',
        conditional: { field: 'hotFlushFreq', notValue: 'Never' },
        options: [
          { label: 'Mild — barely noticeable',        score: 0 },
          { label: 'Moderate — uncomfortable',        score: 1 },
          { label: 'Severe — disrupt daily life',     score: 2 },
          { label: 'Very severe — debilitating',      score: 3 },
        ],
      },
      {
        id: 'nightSweats',
        label: 'Do you experience night sweats?',
        type: 'toggle',
        options: [
          { label: 'Never',            score: 0 },
          { label: 'Occasionally',     score: 1 },
          { label: 'Most nights',      score: 2 },
          { label: 'Every night',      score: 3 },
        ],
      },
      {
        id: 'nightSweatsImpact',
        label: 'Do night sweats wake you from sleep?',
        type: 'toggle',
        conditional: { field: 'nightSweats', notValue: 'Never' },
        options: [
          { label: 'Rarely',              score: 0 },
          { label: 'Sometimes',           score: 1 },
          { label: 'Most of the time',    score: 2 },
          { label: 'Every night',         score: 3 },
        ],
      },
    ],
  },

  {
    id: 'sleep',
    stepLabel: 'Sleep & Energy',
    title: 'Sleep & Energy Levels',
    subtitle: 'Sleep disturbance and fatigue are very common during the menopause transition.',
    icon: '😴',
    questions: [
      {
        id: 'sleepQuality',
        label: 'How would you rate your sleep quality?',
        type: 'toggle',
        options: [
          { label: 'Excellent', score: 0 },
          { label: 'Good',      score: 1 },
          { label: 'Fair',      score: 2 },
          { label: 'Poor',      score: 3 },
          { label: 'Very poor', score: 4 },
        ],
      },
      {
        id: 'sleepHours',
        label: 'How many hours of sleep do you typically get?',
        type: 'toggle',
        options: [
          { label: '8+ hours',   score: 0 },
          { label: '7–8 hours',  score: 0 },
          { label: '6–7 hours',  score: 1 },
          { label: '5–6 hours',  score: 2 },
          { label: 'Under 5',    score: 3 },
        ],
      },
      {
        id: 'fatigue',
        label: 'How often do you feel fatigued or lacking energy?',
        type: 'toggle',
        options: [
          { label: 'Rarely',        score: 0 },
          { label: 'Sometimes',     score: 1 },
          { label: 'Often',         score: 2 },
          { label: 'Almost always', score: 3 },
        ],
      },
    ],
  },

  {
    id: 'mood',
    stepLabel: 'Mood & Mental Health',
    title: 'Mood & Mental Health',
    subtitle: 'Hormonal changes during menopause can significantly affect your emotional wellbeing.',
    icon: '🧠',
    questions: [
      {
        id: 'moodChanges',
        label: 'Have you experienced noticeable mood changes?',
        type: 'toggle',
        options: [
          { label: 'None',                      score: 0 },
          { label: 'Mild irritability',         score: 1 },
          { label: 'Moderate mood swings',      score: 2 },
          { label: 'Significant mood shifts',   score: 3 },
        ],
      },
      {
        id: 'anxiety',
        label: 'Do you experience anxiety or increased worry?',
        type: 'toggle',
        options: [
          { label: 'Rarely / Never',  score: 0 },
          { label: 'Sometimes',       score: 1 },
          { label: 'Often',           score: 2 },
          { label: 'Most of the time',score: 3 },
        ],
      },
      {
        id: 'brainFog',
        label: 'Do you experience brain fog or memory difficulties?',
        type: 'toggle',
        options: [
          { label: 'None',        score: 0 },
          { label: 'Mild',        score: 1 },
          { label: 'Moderate',    score: 2 },
          { label: 'Significant', score: 3 },
        ],
      },
      {
        id: 'lowMood',
        label: 'Have you experienced persistent low mood or depression?',
        type: 'toggle',
        options: [
          { label: 'No',            score: 0 },
          { label: 'Occasionally',  score: 1 },
          { label: 'Often',         score: 2 },
          { label: 'Most days',     score: 3 },
        ],
      },
    ],
  },

  {
    id: 'physical',
    stepLabel: 'Physical Symptoms',
    title: 'Physical Symptoms',
    subtitle: 'Menopause can cause a range of physical changes beyond vasomotor symptoms.',
    icon: '🩺',
    questions: [
      {
        id: 'jointPain',
        label: 'Do you experience joint or muscle pain?',
        type: 'toggle',
        options: [
          { label: 'None',        score: 0 },
          { label: 'Mild',        score: 1 },
          { label: 'Moderate',    score: 2 },
          { label: 'Significant', score: 3 },
        ],
      },
      {
        id: 'weightChanges',
        label: 'Have you noticed unexplained weight gain (especially abdomen)?',
        type: 'toggle',
        options: [
          { label: 'No',                 score: 0 },
          { label: 'Mild (1–3 kg)',      score: 1 },
          { label: 'Moderate (3–8 kg)', score: 2 },
          { label: 'Significant (8+ kg)',score: 3 },
        ],
      },
      {
        id: 'skinHairChanges',
        label: 'Have you noticed changes to your skin or hair?',
        type: 'toggle',
        options: [
          { label: 'No changes',           score: 0 },
          { label: 'Mild dryness/thinning',score: 1 },
          { label: 'Moderate changes',     score: 2 },
          { label: 'Significant changes',  score: 3 },
        ],
      },
      {
        id: 'libido',
        label: 'Have you noticed a change in your libido or vaginal comfort?',
        type: 'toggle',
        options: [
          { label: 'No change',          score: 0 },
          { label: 'Slight decrease',    score: 1 },
          { label: 'Noticeable decrease',score: 2 },
          { label: 'Significant impact', score: 3 },
        ],
      },
    ],
  },

  {
    id: 'medical',
    stepLabel: 'Medical History',
    title: 'Medical History',
    subtitle: 'Your health history helps us tailor recommendations more precisely.',
    icon: '📋',
    questions: [
      {
        id: 'osteoporosisRisk',
        label: 'Do you have risk factors for osteoporosis?',
        type: 'toggle',
        options: [
          { label: 'No known risks',          score: 0 },
          { label: 'Family history',          score: 1 },
          { label: 'Low bone density confirmed',score: 2 },
          { label: 'Previous fracture',       score: 3 },
        ],
      },
      {
        id: 'cardioRisk',
        label: 'Do you have cardiovascular risk factors?',
        type: 'toggle',
        options: [
          { label: 'None',                          score: 0 },
          { label: 'High blood pressure',           score: 2 },
          { label: 'High cholesterol',              score: 2 },
          { label: 'Diabetes or pre-diabetes',      score: 3 },
        ],
      },
      {
        id: 'currentTreatment',
        label: 'Are you currently receiving any menopause treatment?',
        type: 'toggle',
        options: [
          { label: 'No treatment',           score: 0 },
          { label: 'HRT / HT',               score: 0 },
          { label: 'Non-hormonal medication',score: 0 },
          { label: 'Natural / herbal',       score: 0 },
        ],
      },
    ],
  },
];

// ── Review sections ────────────────────────────────────────────────────────────
export const MENOPAUSE_REVIEW_SECTIONS = [
  { id: 'age',       icon: '📅', label: 'Age & Menstrual Status',  fields: ['ageGroup', 'menstrualStatus', 'periodChanges'] },
  { id: 'vasomotor', icon: '🔥', label: 'Vasomotor Symptoms',      fields: ['hotFlushFreq', 'hotFlushSeverity', 'nightSweats'] },
  { id: 'sleep',     icon: '😴', label: 'Sleep & Energy',          fields: ['sleepQuality', 'sleepHours', 'fatigue'] },
  { id: 'mood',      icon: '🧠', label: 'Mood & Mental Health',    fields: ['moodChanges', 'anxiety', 'brainFog', 'lowMood'] },
  { id: 'physical',  icon: '🩺', label: 'Physical Symptoms',       fields: ['jointPain', 'weightChanges', 'skinHairChanges', 'libido'] },
  { id: 'medical',   icon: '📋', label: 'Medical History',         fields: ['osteoporosisRisk', 'cardioRisk', 'currentTreatment'] },
];

// ── Scoring ────────────────────────────────────────────────────────────────────
const WEIGHTS = { age:1.0, vasomotor:1.4, sleep:1.1, mood:1.2, physical:1.0, medical:0.8 };
const MAX_RAW  = { age:8, vasomotor:14, sleep:10, mood:12, physical:12, medical:7 };

export function computeMenopauseScore(answers) {
  let weighted = 0, maxWeighted = 0;
  MENOPAUSE_QUESTION_SCREENS.forEach((screen) => {
    const w = WEIGHTS[screen.id] ?? 1;
    const maxRaw = MAX_RAW[screen.id] ?? 9;
    let raw = 0;
    screen.questions.forEach((q) => {
      const val = answers[q.id];
      if (!val) return;
      const opt = q.options.find((o) => o.label === val);
      if (opt) raw += opt.score ?? 0;
    });
    weighted    += raw * w;
    maxWeighted += maxRaw * w;
  });
  return maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;
}

export function getMenopauseRisk(pct) {
  if (pct < 30) return {
    level: 'Mild Symptoms', emoji: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0',
    desc: 'Your symptoms appear mild. Lifestyle adjustments may be all that is needed right now.',
    means: 'You have few menopause-related indicators. Continue monitoring your symptoms and consider routine wellness check-ups.',
    actions: [
      { icon: '📅', title: 'Schedule a wellness check',       desc: 'Routine annual check to monitor hormonal health.' },
      { icon: '🥗', title: 'Healthy lifestyle habits',        desc: 'Diet, exercise and stress management make a big difference.' },
      { icon: '📊', title: 'Track your symptoms',            desc: 'Use our tracker to spot patterns over time.' },
    ],
  };
  if (pct < 60) return {
    level: 'Moderate Symptoms', emoji: '⚠️', color: '#d97706', bg: '#fffbeb', border: '#fde68a',
    desc: 'You are experiencing a moderate number of menopause symptoms. Professional support is recommended.',
    means: 'Multiple menopause symptoms are impacting your daily life. A consultation can help tailor the right support for you.',
    actions: [
      { icon: '👩‍⚕️', title: 'Book a Menopause Specialist',    desc: 'Get a personalised assessment and treatment plan.' },
      { icon: '💊',   title: 'Explore HRT / HT options',       desc: 'Hormone therapy may significantly relieve your symptoms.' },
      { icon: '📊',   title: 'Symptom & Lifestyle Tracker',    desc: 'Monitor symptoms daily for better insights.' },
    ],
  };
  return {
    level: 'Significant Symptoms', emoji: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
    desc: 'Your symptoms are significantly impacting your wellbeing. Please seek specialist support promptly.',
    means: 'You are experiencing significant menopause-related symptoms across multiple areas. Early intervention can dramatically improve your quality of life.',
    actions: [
      { icon: '🚨', title: 'Urgent Specialist Consultation',  desc: 'Multiple severe symptoms — book a specialist appointment today.' },
      { icon: '🔬', title: 'Full Hormonal Assessment',        desc: 'Blood tests and comprehensive hormone evaluation.' },
      { icon: '💊', title: 'Menopause Care Programme',        desc: 'Structured support combining medical and lifestyle interventions.' },
    ],
  };
}
